import { ASSESSMENT_SLUG } from "core/consts";
import { getCareLevelValue, hasStaticCare } from "core/model/patients";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { grammaticalList } from "core/model/utils/strings";
import { Auction, CareLevelInfos, GetOntologyType } from "core/types";
import { useEncryption } from "dsl/atoms/Contexts";
import { PatientInfoSlugContext } from "dsl/organisms/PatientProfileDetailsShared";
import {
  BodyWrapper,
  EncryptedField,
  OntologyField,
  PatientProfileInfoCard,
  StringField,
  StringValue,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import {
  getCurrentCareLabel,
  getSpecialCareNeedsLabel,
} from "dsl/organisms/PatientProfileDetailsShared/utils";
import { getPredicamentValue } from "react-forms-state";
import { useTranslations } from "translations";
import Translations from "translations/types";

function getCareLevelLabel(
  careLevel: CareLevelInfos | null | undefined,
  translations: Translations,
): string {
  let value = getCareLevelValue(careLevel, translations)?.label;

  if (!value) {
    return translations.general.unknown;
  }

  if (careLevel?.higher_level) {
    value = value.concat(
      `, `,
      translations.patient.hasAppliedforHigherCarelevel,
    );
  }

  if (careLevel?.expedited_request) {
    value = value.concat(`, `, translations.patient.expeditedRequest);
  }

  return value;
}

export function MedicalMobileCareInformation({
  auction,
  getOntology,
  translations,
}: {
  getOntology: GetOntologyType;
  translations: Translations;
  auction: Auction | null | undefined;
}) {
  return (
    <>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.currentCare}${translations.general.colon}`}</b>
        <StringValue
          testId="admissions_current_care_type"
          value={getCurrentCareLabel(
            getOntology,
            translations,
            auction?.admissions?.profile?.current_care_types,
            auction?.admissions?.profile?.current_care_facility_types,
            auction?.admissions?.profile?.current_care_facility_other
              ?.decrypted,
          )}
        />
      </BodyWrapper>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.careLevel}${translations.general.colon}`}</b>
        <StringField
          testId="admissions_care_level"
          value={getCareLevelLabel(
            auction?.patient?.profile?.financing?.carelevel,
            translations,
          )}
        />
      </BodyWrapper>
    </>
  );
}

export function MedicalStaticCareInformation({
  auction,
  getOntology,
  isSealdOnly,
  translations,
  withEncryptionAccess,
}: {
  getOntology: GetOntologyType;
  isSealdOnly: boolean;
  translations: Translations;
  auction: Auction | null | undefined;
  withEncryptionAccess: boolean;
}) {
  const sortedMobility = auction?.admissions?.profile?.mobility
    ?.clone()
    ?.sort((a, b) => a - b);

  return (
    <>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.mobility}${translations.general.colon}`}</b>
        <StringValue
          testId="mobility"
          value={
            grammaticalList({
              array: sortedMobility,
              getOntology,
              ontologyType: ONTOLOGIES.ADMISSIONS_MOBILITY,
              translations,
            }) || translations.careproviderApp.recareAdmissionsProfile.unknown
          }
        />
      </BodyWrapper>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.cognitiveAbilities}${translations.general.colon}`}</b>
        {auction?.admissions?.profile?.mental_ability ? (
          <OntologyField
            getOntology={getOntology}
            type={ONTOLOGIES.ADMISSIONS_MENTAL_ABILITY}
            value={auction.admissions.profile.mental_ability}
          />
        ) : (
          <StringValue
            testId="mental_ability"
            value={translations.careproviderApp.recareAdmissionsProfile.unknown}
          />
        )}
      </BodyWrapper>
      {auction?.admissions?.profile?.preexisting_conditions?.decrypted ? (
        <EncryptedField
          isBold
          isSealdOnly={isSealdOnly}
          prefix={
            translations.careproviderApp.recareAdmissionsProfile.diagnosis
          }
          testId="diagnosis"
          value={auction.admissions.profile.preexisting_conditions}
          withEncryptionAccess={withEncryptionAccess}
        />
      ) : (
        <BodyWrapper>
          <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.diagnosis}${translations.general.colon}`}</b>
          <StringValue
            testId="diagnosis"
            value={translations.careproviderApp.recareAdmissionsProfile.unknown}
          />
        </BodyWrapper>
      )}
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.specialCareNeeds}${translations.general.colon}`}</b>
        <StringValue
          testId="admissions_special_care_needs"
          value={getSpecialCareNeedsLabel(
            getOntology,
            translations,
            auction?.admissions?.profile?.special_care_needs,
            auction?.admissions?.profile?.special_care_needs_other?.decrypted,
          )}
        />
      </BodyWrapper>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.infections}${translations.general.colon}`}</b>
        {auction?.admissions?.profile?.has_infection ? (
          <StringField
            testId="infections"
            value={getPredicamentValue({
              translations,
              value: auction?.admissions?.profile?.has_infection,
            })}
          />
        ) : (
          <StringValue
            testId="infections"
            value={translations.careproviderApp.recareAdmissionsProfile.unknown}
          />
        )}
      </BodyWrapper>
    </>
  );
}

export function AdmissionsMedicalInformationCard({
  auction,
}: {
  auction: Auction;
}) {
  const getOntology = useGetOntology();

  const translations = useTranslations();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();

  return (
    <PatientInfoSlugContext.Provider
      value={{
        assessmentSlug: ASSESSMENT_SLUG.INFO,
        auctionId: auction.id,
        patientId: auction.patient.id,
      }}
    >
      <PatientProfileInfoCard
        testId="medical_information_category_container"
        title={
          translations.careproviderApp.recareAdmissionsForm.medicalInformation
            .title
        }
      >
        <MedicalMobileCareInformation
          auction={auction}
          getOntology={getOntology}
          translations={translations}
        />
        {!!hasStaticCare(auction.solutions) && (
          <MedicalStaticCareInformation
            auction={auction}
            getOntology={getOntology}
            isSealdOnly={isSealdOnly}
            translations={translations}
            withEncryptionAccess={withEncryptionAccess}
          />
        )}
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
